import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import blogBg from "../../images/bg/blog-bg.png";
import gifShape from "../../images/icon/original-9e54f87f13d.gif";

// Blog images (used as fallbacks / placeholders)
import img01 from "../../images/blog/ai1.jpg";
import img02 from "../../images/blog/ai2.jpg";
import img03 from "../../images/blog/ai3.jpg";

// Icons
import icon01 from "../../images/icon/blog-icon01.svg";
import icon02 from "../../images/icon/blog-icon02.svg";

interface NewsItem {
  title: string;
  pubDate: string;
  link: string;
  guid: string;
  author: string;
  thumbnail: string;
  description: string;
  content: string;
  categories: string[];
}

interface DisplayBlog {
  title: string;
  category: string;
  date: string;
  image: string;
  link: string;
  isInternal: boolean;
}

const BlogSection: React.FC = () => {
  const [articles, setArticles] = useState<DisplayBlog[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  // Helper to identify ad banners/sponsors we want to ignore
  const isAdBanner = (url: string): boolean => {
    if (!url) return false;
    const lowercaseUrl = url.toLowerCase();
    return (
      lowercaseUrl.includes("ai-expo-banner") ||
      lowercaseUrl.includes("expo") ||
      lowercaseUrl.includes("banner") ||
      lowercaseUrl.includes("cropped-ai-icon") ||
      lowercaseUrl.endsWith("/image.png") ||
      lowercaseUrl.includes("/image-300x37.png")
    );
  };

  // Helper to decode HTML entity strings returned by WordPress
  const decodeHtml = (html: string): string => {
    try {
      const txt = document.createElement("textarea");
      txt.innerHTML = html;
      return txt.value;
    } catch (e) {
      return html
        .replace(/&amp;/g, "&")
        .replace(/&#8217;/g, "'")
        .replace(/&#8216;/g, "'")
        .replace(/&quot;/g, '"')
        .replace(/&lt;/g, "<")
        .replace(/&gt;/g, ">");
    }
  };

  useEffect(() => {
    const bgEl = document.querySelector<HTMLElement>(".blog.bg_img");
    if (bgEl) {
      bgEl.style.backgroundImage = `url(${blogBg})`;
    }
  }, []);

  useEffect(() => {
    const fetchNews = async () => {
      try {
        setLoading(true);

        // 1. Try WordPress REST API first for high-quality featured images
        try {
          const wpRes = await fetch("https://www.artificialintelligence-news.com/wp-json/wp/v2/posts?_embed&per_page=6");
          if (wpRes.ok) {
            const posts = await wpRes.json();
            if (posts && Array.isArray(posts) && posts.length > 0) {
              const wpArticles: DisplayBlog[] = posts
                .filter((post: any) => {
                  // Filter out sponsored posts
                  const isSponsored = post._embedded?.['wp:term']?.[0]?.some(
                    (t: any) => t.name && t.name.toLowerCase().includes('sponsored')
                  );
                  return !isSponsored;
                })
                .map((post: any, idx: number) => {
                  const fallbackImgs = [img01, img02, img03];
                  const defaultFallback = fallbackImgs[idx % 3];

                  // Extract featured image
                  let image = "";
                  if (post._embedded?.['wp:featuredmedia']?.length > 0) {
                    const mediaUrl = post._embedded['wp:featuredmedia'][0].source_url;
                    if (mediaUrl && !isAdBanner(mediaUrl)) {
                      image = mediaUrl;
                    }
                  }

                  // Extract category
                  let category = idx === 0 ? "ai business tips" : idx === 1 ? "chatbots tips" : "ai business";
                  const cats = post._embedded?.['wp:term']?.[0] || [];
                  const catObj = cats.find((t: any) => t.taxonomy === 'category');
                  if (catObj && catObj.name) {
                    const catName = decodeHtml(catObj.name).toLowerCase();
                    category = catName.includes("intelligence") ? "artificial intelligence" : catName;
                  }

                  return {
                    title: decodeHtml(post.title.rendered),
                    category: category,
                    date: formatDate(post.date),
                    image: image || defaultFallback,
                    link: post.link,
                    isInternal: false
                  };
                });

              if (wpArticles.length >= 3) {
                setArticles(wpArticles.slice(0, 3));
                setLoading(false);
                return;
              }
            }
          }
        } catch (wpErr) {
          console.warn("WordPress REST API failed, falling back to RSS feed:", wpErr);
        }

        // 2. Fallback to RSS feed via rss2json
        const rssUrl = encodeURIComponent("https://www.artificialintelligence-news.com/feed/");
        const res = await fetch(`https://api.rss2json.com/v1/api.json?rss_url=${rssUrl}`);
        const data = await res.json();

        if (data.status === "ok" && data.items && data.items.length > 0) {
          const rssArticles: DisplayBlog[] = data.items
            .filter((item: any) => {
              // Filter out sponsored content
              const isSponsored = item.categories?.some(
                (c: string) => c.toLowerCase().includes("sponsored")
              );
              return !isSponsored;
            })
            .map((item: any, idx: number) => {
              const fallbackImgs = [img01, img02, img03];
              const defaultFallback = fallbackImgs[idx % 3];

              return {
                title: decodeHtml(item.title),
                category: getCategory(item, idx === 0 ? "ai business tips" : idx === 1 ? "chatbots tips" : "ai business"),
                date: formatDate(item.pubDate),
                image: getImageUrl(item, defaultFallback),
                link: item.link,
                isInternal: false
              };
            });

          if (rssArticles.length >= 3) {
            setArticles(rssArticles.slice(0, 3));
          }
        }
      } catch (err) {
        console.error("Error fetching AI news:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchNews();
  }, []);

  // Helpers to safely parse fields (used by RSS fallback)
  const getImageUrl = (item: NewsItem, fallbackImg: string) => {
    if (item.thumbnail && !isAdBanner(item.thumbnail)) return item.thumbnail;
    // Try extracting from description/content
    const imgRegex = /<img[^>]+src="([^">]+)"/g;
    let match;
    const content = item.description || item.content || '';
    while ((match = imgRegex.exec(content)) !== null) {
      const url = match[1];
      if (!isAdBanner(url)) {
        return url;
      }
    }
    return fallbackImg;
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return "";
    try {
      const normalized = dateStr.includes("T")
        ? dateStr.replace("T", " ").replace(/-/g, "/")
        : dateStr.replace(/-/g, "/");
      let date = new Date(normalized);
      if (isNaN(date.getTime())) {
        date = new Date(dateStr);
      }
      if (isNaN(date.getTime())) {
        return dateStr;
      }
      return date.toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric",
      });
    } catch (e) {
      return dateStr;
    }
  };

  const getCategory = (item: NewsItem, fallbackCategory: string) => {
    if (item.categories && item.categories.length > 0) {
      const cat = decodeHtml(item.categories[0]).toLowerCase();
      if (cat.includes("intelligence")) return "artificial intelligence";
      return cat;
    }
    return fallbackCategory;
  };

  // Static fallback data to keep UI functional while loading or if fetch fails
  const staticBlogs = [
    {
      title: "How AI is transforming modern business operations and driving innovation — everything you need to know..",
      category: "ai business tips",
      date: "March 24, 2026",
      image: img01,
      link: "/blog-details",
      isInternal: true
    },
    {
      title: "AI chatbots vs human support — which is best for you?",
      category: "chatbots tips",
      date: "April 27, 2026",
      image: img02,
      link: "/blog-details",
      isInternal: true
    },
    {
      title: "How eCommerce brands use AI to increase sales..",
      category: "ai business",
      date: "March 17, 2026",
      image: img03,
      link: "/blog-details",
      isInternal: true
    }
  ];

  // Map articles to a standardized structure
  const displayBlogs = articles.length >= 3 ? articles : staticBlogs;

  return (
    <section className="blog pt-150 bg_img">
      <div className="container">
        <div className="row mt-none-30">
          {/* Left Column */}
          <div className="col-lg-4 mt-30">
            <div className="sec-title blog-sec-title mb-70">
              <h2 className="title">
                Latest news <img src={gifShape} alt="shape" />
              </h2>
            </div>
            {/* <div className="blog-btn">
              <Link className="thm-btn agency-btn" to="#">
                <span className="text">view more blog</span>
                <span className="arrow">
                  <span className="arrow-icon">
                    <svg
                      width="28"
                      height="28"
                      viewBox="0 0 28 28"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <rect
                        x="5.06592"
                        y="19.9785"
                        width="20.5712"
                        height="2.61221"
                        transform="rotate(-40.2798 5.06592 19.9785)"
                        fill="white"
                      />
                      <rect
                        x="7.97095"
                        y="7.24463"
                        width="2.61221"
                        height="2.61221"
                        transform="rotate(-40.2798 7.97095 7.24463)"
                        fill="white"
                      />
                      <rect
                        x="11.6523"
                        y="7.54834"
                        width="2.61221"
                        height="2.61221"
                        transform="rotate(-40.2798 11.6523 7.54834)"
                        fill="white"
                      />
                      <rect
                        x="15.334"
                        y="7.85205"
                        width="2.61221"
                        height="2.61221"
                        transform="rotate(-40.2798 15.334 7.85205)"
                        fill="white"
                      />
                      <rect
                        x="18.7119"
                        y="11.8374"
                        width="2.61221"
                        height="2.61221"
                        transform="rotate(-40.2798 18.7119 11.8374)"
                        fill="white"
                      />
                      <rect
                        x="18.4084"
                        y="15.52"
                        width="2.61221"
                        height="2.61221"
                        transform="rotate(-40.2798 18.4084 15.52)"
                        fill="white"
                      />
                      <rect
                        x="18.104"
                        y="19.2012"
                        width="2.61221"
                        height="2.61221"
                        transform="rotate(-40.2798 18.104 19.2012)"
                        fill="white"
                      />
                    </svg>
                    <svg
                      width="28"
                      height="28"
                      viewBox="0 0 28 28"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <rect
                        x="5.06592"
                        y="19.9785"
                        width="20.5712"
                        height="2.61221"
                        transform="rotate(-40.2798 5.06592 19.9785)"
                        fill="white"
                      />
                      <rect
                        x="7.97095"
                        y="7.24463"
                        width="2.61221"
                        height="2.61221"
                        transform="rotate(-40.2798 7.97095 7.24463)"
                        fill="white"
                      />
                      <rect
                        x="11.6523"
                        y="7.54834"
                        width="2.61221"
                        height="2.61221"
                        transform="rotate(-40.2798 11.6523 7.54834)"
                        fill="white"
                      />
                      <rect
                        x="15.334"
                        y="7.85205"
                        width="2.61221"
                        height="2.61221"
                        transform="rotate(-40.2798 15.334 7.85205)"
                        fill="white"
                      />
                      <rect
                        x="18.7119"
                        y="11.8374"
                        width="2.61221"
                        height="2.61221"
                        transform="rotate(-40.2798 18.7119 11.8374)"
                        fill="white"
                      />
                      <rect
                        x="18.4084"
                        y="15.52"
                        width="2.61221"
                        height="2.61221"
                        transform="rotate(-40.2798 18.4084 15.52)"
                        fill="white"
                      />
                      <rect
                        x="18.104"
                        y="19.2012"
                        width="2.61221"
                        height="2.61221"
                        transform="rotate(-40.2798 18.104 19.2012)"
                        fill="white"
                      />
                    </svg>
                  </span>
                </span>
              </Link>
            </div> */}
          </div>

          {/* Right Column */}
          <div className="col-lg-8 mt-30" style={{ transition: "opacity 0.3s ease" }}>
            {loading ? (
              /* Skeleton loader shown while fetching — prevents dummy placeholder images from appearing */
              <div className="row mt-none-30">
                {[0, 1, 2].map((i) => (
                  <div key={i} className={i === 0 ? "col-lg-12 mt-30" : "col-lg-6 col-md-6 mt-30"}>
                    <div className="xb-blog-item" style={{ borderRadius: 12, overflow: "hidden", background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)" }}>
                      <div style={{
                        background: "linear-gradient(90deg, rgba(255,255,255,0.05) 25%, rgba(255,255,255,0.1) 50%, rgba(255,255,255,0.05) 75%)",
                        backgroundSize: "200% 100%",
                        animation: "blogSkeletonShimmer 1.5s infinite",
                        height: i === 0 ? 260 : 160,
                        width: "100%"
                      }} />
                      <div style={{ padding: "16px 20px" }}>
                        <div style={{ height: 12, width: "40%", background: "rgba(255,255,255,0.08)", borderRadius: 6, marginBottom: 12 }} />
                        <div style={{ height: 16, width: "90%", background: "rgba(255,255,255,0.08)", borderRadius: 6, marginBottom: 8 }} />
                        <div style={{ height: 16, width: "70%", background: "rgba(255,255,255,0.08)", borderRadius: 6 }} />
                      </div>
                    </div>
                  </div>
                ))}
                <style>{`
                  @keyframes blogSkeletonShimmer {
                    0% { background-position: 200% 0; }
                    100% { background-position: -200% 0; }
                  }
                `}</style>
              </div>
            ) : (
              <div className="row mt-none-30">
                {/* Large Blog Post */}
                <div className="col-lg-12 mt-30">
                  <div className="xb-blog-item wow fadeInUp" data-wow-delay="0ms" data-wow-duration="600ms">
                    <div className="xb-item--inner img-hove-effect xb-border">
                      <div className="xb-img" style={{ height: 280, overflow: "hidden" }}>
                        {[...Array(4)].map((_, i) => (
                          displayBlogs[0].isInternal ? (
                            <Link to={displayBlogs[0].link} key={i}>
                              <img src={displayBlogs[0].image} alt="blog" />
                            </Link>
                          ) : (
                            <a href={displayBlogs[0].link} target="_blank" rel="noopener noreferrer" key={i}>
                              <img src={displayBlogs[0].image} alt="blog" />
                            </a>
                          )
                        ))}
                      </div>
                      <div className="xb-item--holder">
                        <ul className="xb-item--meta list-unstyled ul_li">
                          <li>
                            <img src={icon01} alt="icon" /> {displayBlogs[0].category}
                          </li>
                          <li>
                            <img src={icon02} alt="icon" /> {displayBlogs[0].date}
                          </li>
                        </ul>
                        <h2 className="xb-item--title">
                          {displayBlogs[0].isInternal ? (
                            <Link to={displayBlogs[0].link}>
                              {displayBlogs[0].title}
                            </Link>
                          ) : (
                            <a href={displayBlogs[0].link} target="_blank" rel="noopener noreferrer">
                              {displayBlogs[0].title}
                            </a>
                          )}
                        </h2>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Small Blog 1 */}
                <div className="col-lg-6 col-md-6 mt-30">
                  <div className="xb-blog-item xb-small-blog-item wow fadeInUp" data-wow-delay="150ms" data-wow-duration="600ms">
                    <div className="xb-item--inner img-hove-effect xb-border">
                      <div className="xb-img" style={{ height: 180, overflow: "hidden" }}>
                        {[...Array(4)].map((_, i) => (
                          displayBlogs[1].isInternal ? (
                            <Link to={displayBlogs[1].link} key={i}>
                              <img src={displayBlogs[1].image} alt="blog" />
                            </Link>
                          ) : (
                            <a href={displayBlogs[1].link} target="_blank" rel="noopener noreferrer" key={i}>
                              <img src={displayBlogs[1].image} alt="blog" />
                            </a>
                          )
                        ))}
                      </div>
                      <div className="xb-item--holder">
                        <ul className="xb-item--meta list-unstyled ul_li">
                          <li>
                            <img src={icon01} alt="icon" /> {displayBlogs[1].category}
                          </li>
                          <li>
                            <img src={icon02} alt="icon" /> {displayBlogs[1].date}
                          </li>
                        </ul>
                        <h2 className="xb-item--title">
                          {displayBlogs[1].isInternal ? (
                            <Link to={displayBlogs[1].link}>
                              {displayBlogs[1].title}
                            </Link>
                          ) : (
                            <a href={displayBlogs[1].link} target="_blank" rel="noopener noreferrer">
                              {displayBlogs[1].title}
                            </a>
                          )}
                        </h2>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Small Blog 2 */}
                <div className="col-lg-6 col-md-6 mt-30">
                  <div className="xb-blog-item xb-small-blog-item wow fadeInUp" data-wow-delay="300ms" data-wow-duration="600ms">
                    <div className="xb-item--inner img-hove-effect xb-border">
                      <div className="xb-img" style={{ height: 180, overflow: "hidden" }}>
                        {[...Array(4)].map((_, i) => (
                          displayBlogs[2].isInternal ? (
                            <Link to={displayBlogs[2].link} key={i}>
                              <img src={displayBlogs[2].image} alt="blog" />
                            </Link>
                          ) : (
                            <a href={displayBlogs[2].link} target="_blank" rel="noopener noreferrer" key={i}>
                              <img src={displayBlogs[2].image} alt="blog" />
                            </a>
                          )
                        ))}
                      </div>
                      <div className="xb-item--holder">
                        <ul className="xb-item--meta list-unstyled ul_li">
                          <li>
                            <img src={icon01} alt="icon" /> {displayBlogs[2].category}
                          </li>
                          <li>
                            <img src={icon02} alt="icon" /> {displayBlogs[2].date}
                          </li>
                        </ul>
                        <h2 className="xb-item--title">
                          {displayBlogs[2].isInternal ? (
                            <Link to={displayBlogs[2].link}>
                              {displayBlogs[2].title}
                            </Link>
                          ) : (
                            <a href={displayBlogs[2].link} target="_blank" rel="noopener noreferrer">
                              {displayBlogs[2].title}
                            </a>
                          )}
                        </h2>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default BlogSection;
